import { LitElement, css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import { nsSharedStyles } from '../ns-styles/index.js';
import { formatCountdown, quoteSecondsLeft } from '../helpers/credit-pricing.js';

/**
 * How long a quoted price still holds: "Price valid until 14:32" with a live m:ss countdown, then "This price has expired"
 * with a Refresh price button. Set `.expiresAt` to the quote's expiry. It fires `quote-expired` once when the time runs
 * out, and `quote-refresh` when the button is pressed; the card asks for a new quote. `refreshing` disables the button.
 */
@customElement('ns-quote-validity')
export class NSQuoteValidity extends LitElement {
  @property({ attribute: false })
  expiresAt: string | Date | null = null;

  @property({ type: Boolean })
  refreshing = false;

  @state()
  private __secondsLeft = 0;

  private __timer?: ReturnType<typeof setInterval>;

  private __expiredSent = false;

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--wa-space-xs) var(--wa-space-s);
        font-size: var(--wa-font-size-s);
      }

      :host([hidden]) {
        display: none;
      }

      .countdown {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
      }

      .expired {
        font-weight: 600;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this.__tick();
    this.__timer = setInterval(() => this.__tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.__timer) clearInterval(this.__timer);
    this.__timer = undefined;
  }

  willUpdate(changed: PropertyValues<this>) {
    if (changed.has('expiresAt')) {
      this.__expiredSent = false;
      this.__tick();
    }
  }

  private __tick() {
    this.__secondsLeft = quoteSecondsLeft(this.expiresAt);
    if (this.expiresAt && this.__secondsLeft === 0 && !this.__expiredSent) {
      this.__expiredSent = true;
      this.dispatchEvent(new CustomEvent('quote-expired', { bubbles: true, composed: true }));
    }
  }

  private __refresh() {
    this.dispatchEvent(new CustomEvent('quote-refresh', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.expiresAt) return nothing;
    if (this.__secondsLeft > 0) {
      const until = new Date(this.expiresAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      return html`
        <span>
          Price valid until ${until}
          <span class="countdown" role="timer" aria-live="off">(${formatCountdown(this.__secondsLeft)} left)</span>
        </span>
      `;
    }
    return html`
      <span class="expired" role="status">This price has expired.</span>
      <wa-button
        variant="neutral"
        appearance="outlined"
        size="s"
        type="button"
        ?disabled=${this.refreshing}
        @click=${() => this.__refresh()}
      >
        Refresh price
      </wa-button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-quote-validity': NSQuoteValidity;
  }
}
